import { useState } from "react";
import { useLocation, useNavigate, type Location } from "react-router";
import {
  ArrowRight,
  ClipboardList,
  LogIn,
  MapPin,
  MessageSquare,
  Video,
} from "lucide-react";
import {
  Body,
  Caption,
  Subtitle,
  Title,
  VButton,
  VField,
  VInput,
  cn,
} from "@/components/ui";
import { ROLE_ICONS, ROLE_LABELS } from "@/domains/auth/components/roleLabels";
import { useAuthSession } from "@/domains/auth/hooks/useCurrentUser";
import { SEED_USERS } from "@/domains/auth/mocks/users";
import type { User } from "@/domains/auth/api/types";

type RedirectState = { from?: Location };

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useAuthSession();
  const state = location.state as RedirectState | null;
  const redirectTo = state?.from?.pathname ?? "/";

  const [selected, setSelected] = useState<User | null>(SEED_USERS[0] ?? null);
  const [password, setPassword] = useState("demo");

  const handleLogin = (): void => {
    if (!selected) return;
    session.login(selected);
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen bg-card">
      <div className="grid min-h-screen lg:grid-cols-2">
        <div className="flex items-center justify-center bg-card p-6 sm:p-10">
          <div className="w-full max-w-md space-y-8">
            <img
              src="/pza-logo.jpg"
              alt="Federale Politie en Politiezone Antwerpen"
              className="block h-12 w-auto"
            />

            <div className="space-y-1">
              <Title>Welkom terug</Title>
              <Subtitle>Selecteer een demo-account en log in.</Subtitle>
            </div>

            <div className="space-y-2">
              {SEED_USERS.map((user) => {
                const Icon = ROLE_ICONS[user.role];
                const isSelected = selected?.id === user.id;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => setSelected(user)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors",
                      isSelected
                        ? "border-primary-600 bg-primary-50 ring-2 ring-primary-200"
                        : "border-border bg-card hover:border-primary-300 hover:bg-muted/40",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
                        isSelected ? "bg-primary-600 text-on-primary" : user.avatarColor,
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <Body weight="semibold">
                        {user.firstName} {user.lastName}
                      </Body>
                      <Caption tone="muted">
                        {ROLE_LABELS[user.role]} · {user.service}
                      </Caption>
                    </div>
                    {isSelected ? (
                      <ArrowRight className="size-4 text-primary-700" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="space-y-3">
              <VField label="E-mail" htmlFor="login-email">
                <VInput
                  id="login-email"
                  type="email"
                  value={selected?.email ?? ""}
                  readOnly
                />
              </VField>
              <VField
                label="Wachtwoord"
                htmlFor="login-password"
                hint="Demo: elk wachtwoord wordt aanvaard."
              >
                <VInput
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </VField>
            </div>

            <VButton
              fullWidth
              onClick={handleLogin}
              disabled={!selected || password.length === 0}
            >
              <LogIn className="size-4" aria-hidden />
              Inloggen als {selected ? selected.firstName : "—"}
            </VButton>
          </div>
        </div>

        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary-700 to-primary-800 p-10 text-on-primary lg:flex lg:pl-[136px] lg:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,96px_50%)]">
          <Video
            className="pointer-events-none absolute -bottom-16 -right-16 size-80 text-on-primary/[0.05]"
            strokeWidth={1}
            aria-hidden
          />

          <Caption className="relative uppercase tracking-wide text-on-primary/70">
            Politiezone Antwerpen
          </Caption>

          <div className="relative space-y-8">
            <div className="space-y-3">
              <Title className="text-on-primary">Camerabeheer-platform</Title>
              <Subtitle className="max-w-md text-on-primary/80">
                Interne tool voor mobiel cameratoezicht — beheer aanvragen,
                dossiers, plaatsing en communicatie met externe partners vanuit één
                centrale omgeving.
              </Subtitle>
            </div>

            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-on-primary/10 ring-1 ring-on-primary/15">
                  <ClipboardList className="size-4" aria-hidden />
                </span>
                <Body className="text-on-primary/90">
                  Aanvragen en dossiers in één overzicht
                </Body>
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-on-primary/10 ring-1 ring-on-primary/15">
                  <MapPin className="size-4" aria-hidden />
                </span>
                <Body className="text-on-primary/90">
                  Plaatsing en planning op de kaart
                </Body>
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-on-primary/10 ring-1 ring-on-primary/15">
                  <MessageSquare className="size-4" aria-hidden />
                </span>
                <Body className="text-on-primary/90">
                  Communicatie met externe partners
                </Body>
              </li>
            </ul>
          </div>

          <Caption className="relative text-on-primary/60">
            Demo-omgeving — geen echte authenticatie. Selecteer een persoon om de
            applicatie als die rol te bekijken.
          </Caption>
        </div>
      </div>
    </div>
  );
}
